<template>
  <!-- フィルターダイアログ表示ボタン -->
  <div class="table-filter-bar">
    <div class="cursor-pointer" @click="dialogVisible = true">
      <el-badge
        :value="filterChips.length"
        class="chip-badge"
        :hidden="filterChips.length === 0"
      >
        <span class="icon-filter">
          <svg
            version="1.1"
            id="Layer_1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            x="0px"
            y="0px"
            viewBox="0 0 18 18"
            style="enable-background:new 0 0 18 18;"
            xml:space="preserve"
          >
            <path
              d="M8.2,15.4c-0.2,0-0.4-0.1-0.6-0.2c-0.2-0.2-0.2-0.3-0.2-0.6V9.8L2.6,3.7C2.4,3.5,2.4,3.2,2.5,3c0.1-0.3,0.3-0.4,0.6-0.4
        h11.7c0.3,0,0.5,0.1,0.6,0.4c0.1,0.3,0.1,0.5-0.1,0.7l-4.8,6.1v4.8c0,0.2-0.1,0.4-0.2,0.6c-0.2,0.2-0.3,0.2-0.6,0.2H8.2z M9,9.9
        l4.8-6.1H4.2L9,9.9z"
            />
          </svg>
        </span>

        <!-- <el-button icon="el-icon-s-operation" size="mini" circle type="text" /> -->
      </el-badge>
    </div>
    <!-- フィルターで指定した内容をChipsで表示 -->
    <div v-if="filterChips.length" class="active-filters">
      <span
        v-for="(item, index) in filterChips"
        :key="index"
        class="filter-chip"
        :title="$t(item.labelKey) + ': ' + item.value"
      >
        <span class="cursor-pointer" @click="dialogVisible = true">
          {{ $t(item.labelKey) }}: {{ item.value }}
        </span>
        <i
          v-if="item.key !== 'flowUpdateDatetime' && item.key !== 'lastUpdateDatetime'"
          class="el-icon-close"
          @click="onRemove(item.key)"
        ></i>
      </span>
      <el-button
        class="ml-1"
        type="text"
        size="mini"
        icon="el-icon-refresh"
        @click="onReset"
      />
    </div>
    <!-- スキーマをもとにフォームをダイアログ内に表示 -->
    <el-dialog
      class="search-condition-dialog"
      :class="{ isFixed: isFixedDialog }"
      :visible.sync="dialogVisible"
      append-to-body
    >
      <el-form
        ref="formRef"
        class="search_unit"
        label-position="top"
        :model="localFilterForm"
        :rules="filterRules"
      >
        <!-- scoped slot で親から渡された model を編集できる -->
        <!--<slot name="form" v-bind:model="localFilterModel" />-->
        <div class="flex flex-wrap gap-8">
          <!-- a / b に縦罫線、最後の c は罫線なし例 -->
          <div v-for="g in groupOrder" :key="g">
            <div
              v-if="groups[g].length > 0"
              class="flex flex-wrap gap-8 pr-6 py-4 mr-6"
              :style="g !== 'c' ? { borderBottom: '1px solid #ddd' } : {}"
            >
              <template v-for="item in groups[g]">
                <!-- type: 'break' はレイアウト専用のダミー項目。v-model・バリデーション・chip対象外で、行の折り返し位置だけをスキーマ側で指定できる -->
                <div
                  v-if="item.type === 'break'"
                  :key="item.key"
                  class="search-condition-break"
                ></div>
                <el-form-item
                  v-else
                  :key="item.key"
                  :label="$t(item.labelKey)"
                  :prop="item.key"
                  :class="`search-condition-${item.key}`"
                >
                  <component
                    :is="getFormComponent(item)"
                    v-model="localFilterForm[item.key]"
                    v-bind="getComponentProps(item)"
                    v-on="getComponentListeners(item)"
                    :style="{ minWidth: '235px' }"
                  >
                    <template v-if="item.type === 'select'">
                      <el-option
                        v-for="opt in item.options"
                        :key="opt.value"
                        :label="opt.label"
                        :value="opt.value"
                        :title="opt.label"
                      />
                    </template>
                    <template v-if="item.type === 'checkbox'">
                      {{ $t(item.checkboxLabel) }}
                    </template>
                  </component>
                </el-form-item>
              </template>
            </div>
          </div>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="onReset">Clear</el-button>
        <el-button type="primary" :disabled="!isFormValid" @click="onApply">
          Search
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { defineComponent, ref, computed, toRefs } from '@vue/composition-api';
import dayjs from 'dayjs';
import { LANGUAGE_SETTING } from 'lib-tw-common';
import { useFilterSchema } from '@/composables/useFilterSchema';

export default defineComponent({
  name: 'TwTableFilterBar',
  props: {
    filterSchema: {
      type: Array,
      required: true,
    },
    filterModel: {
      type: Object,
      required: true,
    },
    filterChips: {
      type: Array,
      default: () => [],
    },
    isFixedDialog: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['apply', 'reset', 'remove', 'change-filter'],
  setup(props, { emit, root }) {
    const formRef = ref(null);
    const localFilterForm = props.filterModel;
    const isFormValid = ref(false);
    const dialogVisible = ref(false);

    const minFlowUpdateDatetime = ref(null);

    const groupConfig = {
      a: ['tradingProgressStatus', 'flowName', 'invoiceNo'],
      b: [
        'flowCreateDatetime',
        'lastUpdateDatetime',
        'sectionRowBreak',
        'fromSectionIds',
        'toSectionIds',
        'tradingFlowId',
        'poNo',
        'contractDate',
        'sellerName',
        'buyerName',
        'flowReference',
        'flowUserName',
        'goodsName',
        'forcedCloseIncludeHiddenFlow',
      ],
    };
    const groupOrder = ['a', 'b', 'c'];

    const { rules: filterRules } = useFilterSchema(props.filterSchema);

    // 表示用のグループ分け
    const groups = computed(() => {
      const res = { a: [], b: [], c: [] };
      for (const item of props.filterSchema) {
        if (groupConfig.a.includes(item.key)) res.a.push(item);
        else if (groupConfig.b.includes(item.key)) res.b.push(item);
        else res.c.push(item);
      }
      return res;
    });

    const selectableUpdateDateRange = computed(() => {
      return (
        root.$store.getters.getSystemMasterData(
          'SYS_MST_TRIAL_PERIOD',
          'TRADING_LIST',
        )?.value || 180
      );
    });

    // input, select, date-range のマッピング関数
    const getFormComponent = item => {
      switch (item.type) {
        case 'text':
          return 'el-input';
        case 'select':
          return 'el-select';
        case 'daterange':
          return 'el-date-picker';
        case 'checkbox':
          return 'el-checkbox';
        default:
          return 'el-input';
      }
    };

    const getComponentProps = item => {
      let baseProps = {};
      if (item.type === 'select') {
        baseProps = {
          placeholder: 'Select',
          multiple: item.multiple === true,
        };
      } else if (item.type === 'daterange') {
        baseProps = {
          type: 'daterange',
          rangeSeparator: 'to',
          format: 'yyyy-MM-dd',
          valueFormat: 'yyyy-MM-dd',
        };
      } else if (item.type === 'checkbox') {
        baseProps = {
          label: item.checkboxLabel,
        };
      }
      // 取引一覧・取引フロー一覧の最終更新日フィルターの場合
      if (item.key === 'flowUpdateDatetime' || item.key === 'lastUpdateDatetime') {
        item.props.pickerOptions = {
          onPick: pickUpdateDate,
          disabledDate: disabledUpdateDate,
          shortcuts: [
            {
              text: 'Today',
              onClick(picker) {
                picker.$emit('pick', [
                  dayjs().format('YYYY-MM-DD 00:00:00'),
                  dayjs().format('YYYY-MM-DD 23:59:59'),
                ]);
              },
            },
          ],
        };
      }

      return {
        ...baseProps,
        ...(item.props || {}),
      };
    };

    const getComponentListeners = item => {
      const listeners = {
        input: validateForm,
        blur: validateForm,
      };
      if (item.type === 'daterange') {
        // listeners.change = onDateRangeChange;
      }
      return listeners;
    };

    // バリデーション実行
    const validateForm = data => {
      if (formRef.value) {
        formRef.value.validate(valid => {
          isFormValid.value = valid;
        });
        emit('change-filter', data);
      }
    };

    const onApply = () => {
      emit('apply');
      dialogVisible.value = false;
    };

    const onRemove = key => {
      emit('remove', key);
    };

    const onReset = () => {
      emit('reset');
    };

    const pickUpdateDate = dates => {
      if (dates.maxDate) {
        // minFlowUpdateDatetimeの更新はsetUpdateInfo内で実施（shortcutでもリセットさせる必要があるため）
        setUpdateDateInfo('reset');
      } else {
        minFlowUpdateDatetime.value = dates.minDate;
        setUpdateDateInfo();
      }
    };

    const setUpdateDateInfo = (type = 'update') => {
      if (!['update', 'reset'].includes(type)) return;
      createDivsForUpdateDate();
      const footerEl = getActiveUpdateDatePickerFooter();
      const dateLimitEl = footerEl?.querySelector('.selectable-date-range');
      const selectedDateEl = footerEl?.querySelector('.selected-date');
      let messageForSelectableDates = '';
      let messageForSelectedDate = '';

      if (type === 'update') {
        const minSelectableDate = dayjs(minFlowUpdateDatetime.value)
          .add(-selectableUpdateDateRange.value + 1, 'day')
          .format('YYYY-MM-DD');
        const maxSelectableDate = dayjs(minFlowUpdateDatetime.value)
          .add(selectableUpdateDateRange.value - 1, 'day')
          .format('YYYY-MM-DD');
        messageForSelectableDates =
          root.$store.state.language === LANGUAGE_SETTING.ENGLISH
            ? `You can select from ${minSelectableDate} to ${maxSelectableDate}`
            : `${minSelectableDate}から${maxSelectableDate}まで選択できます`;

        const selectedDate = dayjs(minFlowUpdateDatetime.value).format(
          'YYYY-MM-DD',
        );
        messageForSelectedDate =
          root.$store.state.language === LANGUAGE_SETTING.ENGLISH
            ? `${selectedDate} is selected`
            : `${selectedDate}が選択されています`;
      } else {
        minFlowUpdateDatetime.value = null;
      }
      if (dateLimitEl) dateLimitEl.innerText = messageForSelectableDates;
      if (selectedDateEl) selectedDateEl.innerText = messageForSelectedDate;
    };

    // 表示中（閉じずにDOMへ残留した古いパネルではない）のピッカーパネルfooterを取得する
    const getActiveUpdateDatePickerFooter = () => {
      const footers = document.querySelectorAll('.el-picker-panel__footer');
      return [...footers].find(el => el.getClientRects().length > 0) || null;
    };

    // 最終更新日時選択カレンダー内に必要なNODEが存在しない場合にのみ作成する
    const createDivsForUpdateDate = () => {
      const footerEl = getActiveUpdateDatePickerFooter();
      if (!footerEl) return;
      for (const className of ['selectable-date-range', 'selected-date']) {
        if (footerEl.querySelector(`.${className}`)) continue;
        const newEl = document.createElement('div');
        newEl.className = className;
        footerEl.prepend(newEl);
      }
    };

    const disabledUpdateDate = date => {
      if (minFlowUpdateDatetime.value) {
        return checkSystemMasterDaysOver(minFlowUpdateDatetime.value, date);
      }
      return false;
    };

    const checkSystemMasterDaysOver = (
      date1 = localFilterForm.flowUpdateDatetime?.[1],
      date2 = localFilterForm.flowUpdateDatetime?.[0],
    ) => {
      return (
        Math.abs(dayjs(date1).diff(dayjs(date2), 'day')) >
        selectableUpdateDateRange.value - 1
      );
    };

    return {
      formRef,
      groups,
      groupOrder,
      isFormValid,
      localFilterForm,
      filterRules,
      validateForm,
      getFormComponent,
      getComponentProps,
      getComponentListeners,
      dialogVisible,
      onApply,
      onRemove,
      onReset,
    };
  },
});
</script>
<style lang="scss">
.table-filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.active-filters {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: hidden;
  max-width: 100%;
  white-space: nowrap;
  transition: overflow 0.2s;
  -webkit-overflow-scrolling: touch;
  gap: 8px;
}
.active-filters:hover {
  overflow-x: auto;
}
.active-filters::-webkit-scrollbar {
  height: 6px;
}
.active-filters::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}
.active-filters::-webkit-scrollbar-track {
  background-color: transparent;
}
.filter-chip {
  background: #fcfcfc;
  border: 1px solid #3ec1ff;
  color: #3ec1ff;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}
.filter-chip .el-icon-close {
  cursor: pointer;
  font-size: 12px;
  color: #bfbfcd;
}

.search_unit .el-form-item input {
  width: 100%;
}

// type: 'break'のダミー項目用。flex-wrapの行を強制的に折り返す非表示要素
.search-condition-break {
  flex: 0 0 100%;
  height: 0;
  margin: 0;
  padding: 0;
  border: none;
}

.filter-select-dropdown {
  max-width: 300px;
  white-space: normal;
  word-break: break-word;
}

.search-condition-dialog {
  &.isFixed .el-dialog {
    width: 70vw;
    height: 80vh;
  }

  & .el-dialog {
    display: flex;
    flex-direction: column;

    & .el-dialog__body {
      padding-bottom: 0;
      flex-grow: 1;
      // max-height: calc(100% - 100px);
      overflow-y: auto;
      & .row {
        &.detailed-conditions {
          border-top: 1px solid #bfbfcd;
          padding-top: 8px;
        }
      }
      & .el-select {
        width: 100%;
        & input {
          width: 100%;
        }
      }
      & .el-date-editor:has(input:not(:placeholder-shown)),
      & .el-input__inner:not(.el-date-editor):not(:placeholder-shown),
      & .el-select:has(.el-tag) .el-input .el-input__inner {
        background-color: rgba(#b2efcb, 0.3);
      }
    }

    & .el-dialog__footer {
      height: 100px;
    }
  }
}

.el-select .el-select__tags {
  flex-wrap: wrap;
  max-height: 100px;
  overflow-y: auto;
}

.el-date-range-picker.within-system-trading-list-days {
  .el-picker-panel__footer {
    // clearableがfalseでもボタンが残るのでCSSで非表示とする（開発者ツールで表示可能だが、バリデーションでひっかかるようにしている）
    & .el-button.el-picker-panel__link-btn.el-button--text.el-button--mini {
      display: none;
    }

    & .selectable-date-range,
    & .selected-date {
      padding-left: 4px;
      text-align: left;
      font-size: 14px;
    }
  }
}
</style>

<style scoped>
.chip-badge ::v-deep .el-badge__content {
  top: 6px;
  right: 12px;
  font-size: 9px;
  height: 16px;
  line-height: 10px;
  padding: 3px 5px;
}
.icon-filter svg {
  width: 20px;
  height: 20px;
  fill: #5185C5;
}
</style>
